import { isObject, isString } from 'lodash'

import { parseFromSmallestDenomination } from '~/data/utils/currency'

import type { BillingDraftFields } from './types'

/**
 * Presentation helpers for a draft's `fields` (billing-api request shape, money in minor units).
 * Used by the Draft card to summarise a create-mode draft and to render `from → to` values.
 */

export type DraftFieldRow = {
  path: string
  value: unknown
}

/** Keys whose numeric value is money in minor units (cents). */
const MONEY_KEYS = new Set([
  'unitAmount',
  'flatAmount',
  'rate',
  'price',
  'noShowPenalty',
  'initialPrepaymentAmount',
  'topOffThreshold',
  'amount',
  'discountAmount',
  'commitmentAmount'
])

/** Nested collections that read better as a count than as a dump. */
const COLLECTION_KEYS = new Set(['entitySelections', 'authorizedEntities', 'products', 'discounts', 'commitments'])

const MAX_ROWS = 40

/**
 * Flattens draft fields to dotted paths, one row per scalar. Objects recurse; `metadata` becomes one
 * row per key; arrays of objects collapse to a count (TOU rates also report their period count).
 */
export function flattenDraftFields(fields: BillingDraftFields): DraftFieldRow[] {
  const rows: DraftFieldRow[] = []
  visit(fields as Record<string, unknown>, '', rows)
  return rows.slice(0, MAX_ROWS)
}

function visit(value: Record<string, unknown>, prefix: string, rows: DraftFieldRow[]): void {
  for (const [key, entry] of Object.entries(value)) {
    if (entry === undefined || key.startsWith('_')) continue
    const path = prefix ? `${prefix}.${key}` : key
    if (Array.isArray(entry)) {
      rows.push({ path, value: entry })
      continue
    }
    if (isObject(entry) && !COLLECTION_KEYS.has(key)) {
      visit(entry as Record<string, unknown>, path, rows)
      continue
    }
    rows.push({ path, value: entry })
  }
}

/** Last segment of a dotted path: `flatRatePricing.unitAmount` → `unitAmount`. */
export function pathKey(path: string): string {
  return path.split('.').pop() ?? path
}

export function isMoneyPath(path: string): boolean {
  return MONEY_KEYS.has(pathKey(path))
}

/** Best-effort currency for the draft: any nested `currency` / `settlementCurrency` string. */
export function findDraftCurrency(fields: BillingDraftFields): string | undefined {
  const raw = fields as Record<string, unknown>
  const direct = raw.settlementCurrency ?? raw.currency
  if (isString(direct) && direct) return direct
  for (const entry of Object.values(raw)) {
    if (isObject(entry) && !Array.isArray(entry)) {
      const nested = (entry as Record<string, unknown>).currency
      if (isString(nested) && nested) return nested
    }
  }
  return undefined
}

export type DraftValueLabels = {
  yes: string
  no: string
  empty: string
  items: (count: number) => string
  periods: (rates: number, periods: number) => string
}

/**
 * Human-readable rendering of one draft value. Money keys convert minor units through the supplied
 * currency formatter; arrays of primitives are joined; arrays of objects become a count.
 */
export function formatDraftValue(
  path: string,
  value: unknown,
  labels: DraftValueLabels,
  formatCurrency: (major: number) => string
): string {
  if (value === null || value === undefined || value === '') return labels.empty
  if (typeof value === 'boolean') return value ? labels.yes : labels.no
  if (typeof value === 'number') {
    return isMoneyPath(path) ? formatCurrency(parseFromSmallestDenomination(value)) : String(value)
  }
  if (isString(value)) return value
  if (Array.isArray(value)) {
    if (value.every((item) => typeof item !== 'object' || item === null)) {
      return value.length === 0 ? labels.empty : value.map(String).join(', ')
    }
    if (pathKey(path) === 'rates') {
      const periods = value.reduce<number>((sum, rate) => {
        const rawPeriods = isObject(rate) ? (rate as Record<string, unknown>).periods : undefined
        return sum + (Array.isArray(rawPeriods) ? rawPeriods.length : 0)
      }, 0)
      return labels.periods(value.length, periods)
    }
    return labels.items(value.length)
  }
  const json = JSON.stringify(value)
  return json.length > 80 ? `${json.slice(0, 77)}…` : json
}
